'use client'

import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { AttendanceAPI } from '@/lib/graphql/queries/attendance'
import { getDeviceInfo } from '@/lib/utils'
import { Play, Pause, RefreshCw } from 'lucide-react'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import LiveClock from './dashboard/time'

interface CheckInOutProps {
  className?: string
  showClock?: boolean
  showStatus?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: 'compact' | 'full'
}

export default function CheckInOut({
  className = '',
  showClock = true,
  showStatus = true,
  size = 'md',
  variant = 'full'
}: CheckInOutProps) {
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [checkInTime, setCheckInTime] = useState<string | null>(null)
  const [checkInDateTime, setCheckInDateTime] = useState<Date | null>(null)
  const [checkOutTime, setCheckOutTime] = useState<string | null>(null)
  const [checkInOutLoading, setCheckInOutLoading] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [checkoutReason, setCheckoutReason] = useState('')
  const [attendanceStatus, setAttendanceStatus] = useState<{
    earlyCheckIn?: boolean
    lateCheckIn?: boolean
    earlyCheckOut?: boolean
    lateCheckOut?: boolean
  }>({})

  // Get userId from Redux state
  const userId = useSelector((state: any) => state?.auth?.userId)

  // Check if user has worked less than 9 hours
  const isEarlyCheckout = () => {
    if (!checkInDateTime) return false
    const now = new Date()
    const timeDiffInHours = (now.getTime() - checkInDateTime.getTime()) / (1000 * 60 * 60)
    return timeDiffInHours < 9
  }

  useEffect(() => {
    if (userId) {
      fetchAttendanceStatus()
    }
  }, [userId])

  const fetchAttendanceStatus = async () => {
    try {
      if (!userId) {
        console.error('No userId available')
        return
      }

      // Fetch today's attendance to check status
      const attendance = await AttendanceAPI.getTodayAttendance()

      if (attendance) {
        // Determine status based on checkIn and checkOut objects
        const hasCheckedIn = attendance.checkIn && attendance.checkIn.time !== null
        const hasCheckedOut = attendance.checkOut && attendance.checkOut.time !== null

        // User is currently checked in if they have checked in but NOT checked out
        setIsCheckedIn(Boolean(hasCheckedIn && !hasCheckedOut))

        // Store attendance status flags
        setAttendanceStatus({
          earlyCheckIn: attendance.earlyCheckIn,
          lateCheckIn: attendance.lateCheckIn,
          earlyCheckOut: attendance.earlyCheckOut,
          lateCheckOut: attendance.lateCheckOut
        })

        // Store check-in time if available
        if (hasCheckedIn && attendance.checkIn?.time) {
          const checkInDate = new Date(attendance.checkIn.time)
          setCheckInDateTime(checkInDate)
          setCheckInTime(checkInDate.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }))
        } else {
          setCheckInTime(null)
          setCheckInDateTime(null)
        }

        // Store check-out time if available
        if (hasCheckedOut && attendance.checkOut?.time) {
          const checkOutDate = new Date(attendance.checkOut.time)
          setCheckOutTime(checkOutDate.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }))
        } else {
          setCheckOutTime(null)
        }
      } else {
        // No attendance record means user hasn't checked in yet
        setIsCheckedIn(false)
        setCheckInTime(null)
        setCheckOutTime(null)
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error)
      setIsCheckedIn(false)
      setCheckInTime(null)
      setCheckOutTime(null)
    }
  }

  const handleCheckIn = async () => {
    setCheckInOutLoading(true)
    try {
      if (!userId) {
        toast.error('User not authenticated')
        return
      }

      // Get device info for tracking
      const deviceInfo = await getDeviceInfo()

      // Call attendance check-in API
      const attendance = await AttendanceAPI.checkIn({
        employeeId: userId,
        latitude: deviceInfo.latitude,
        longitude: deviceInfo.longitude,
        deviceId: deviceInfo.deviceId,
        deviceType: deviceInfo.deviceType,
        ipAddress: deviceInfo.ipAddress
      })

      if (attendance) {
        setIsCheckedIn(true)

        // Set attendance status flags
        setAttendanceStatus({
          earlyCheckIn: attendance.earlyCheckIn,
          lateCheckIn: attendance.lateCheckIn,
          earlyCheckOut: attendance.earlyCheckOut,
          lateCheckOut: attendance.lateCheckOut
        })

        // Set check-in time from the response
        if (attendance.checkIn?.time) {
          const checkInDate = new Date(attendance.checkIn.time)
          setCheckInDateTime(checkInDate)
          setCheckInTime(checkInDate.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }))
        }

        // Show success message
        if (attendance.lateCheckIn) {
          toast.success('Checked in successfully (Late)', {
            icon: '⏰',
            duration: 4000,
          })
        } else if (attendance.earlyCheckIn) {
          toast.success('Checked in successfully (Early)', {
            icon: '✨',
            duration: 4000,
          })
        } else {
          toast.success('Successfully checked in!', {
            duration: 4000,
          })
        }
      }
    } catch (error: any) {
      console.error('Check-in failed:', error)

      // Extract error message from GraphQL error response
      let errorMessage = 'Failed to check in. Please try again.'

      try {
        // Check different possible error structures
        if (error.response?.errors && Array.isArray(error.response.errors)) {
          // GraphQL error response structure
          const firstError = error.response.errors[0]
          if (firstError?.message) {
            errorMessage = firstError.message
          }
        } else if (error.errors && Array.isArray(error.errors)) {
          // Direct errors array
          const firstError = error.errors[0]
          if (firstError?.message) {
            errorMessage = firstError.message
          }
        } else if (error.message) {
          // Simple error message
          errorMessage = error.message
        }

        // Handle specific error cases
        if (errorMessage.toLowerCase().includes('already checked in')) {
          // User is already checked in, refresh the status
          await fetchAttendanceStatus()
          toast.error('You are already checked in for today!', {
            icon: '⏰',
            duration: 4000,
          })
          return
        }
      } catch (parseError) {
        console.error('Error while parsing error:', parseError)
      }

      toast.error(errorMessage, {
        duration: 5000,
      })
    } finally {
      setCheckInOutLoading(false)
    }
  }

  const handleCheckOut = async (event?: React.MouseEvent) => {
    // Check if user has worked less than 9 hours and needs confirmation
    if (isEarlyCheckout()) {
      setShowConfirmDialog(true)
      return
    }

    await performCheckOut()
  }

  const performCheckOut = async () => {
    setCheckInOutLoading(true)
    try {
      if (!userId) {
        toast.error('User not authenticated')
        return
      }

      // Get device info for tracking
      const deviceInfo = await getDeviceInfo()

      // Call attendance check-out API
      const attendance = await AttendanceAPI.checkOut({
        employeeId: userId,
        latitude: deviceInfo.latitude,
        longitude: deviceInfo.longitude,
        deviceId: deviceInfo.deviceId,
        deviceType: deviceInfo.deviceType,
        ipAddress: deviceInfo.ipAddress
      })

      if (attendance) {
        setIsCheckedIn(false)

        // Set check-out time from the response
        if (attendance.checkOut?.time) {
          const checkOutDate = new Date(attendance.checkOut.time)
          setCheckOutTime(checkOutDate.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }))
        }

        // Update attendance status with check-out flags
        setAttendanceStatus({
          earlyCheckIn: attendance.earlyCheckIn,
          lateCheckIn: attendance.lateCheckIn,
          earlyCheckOut: attendance.earlyCheckOut,
          lateCheckOut: attendance.lateCheckOut
        })

        // Show appropriate message based on check-out status
        if (attendance.earlyCheckOut) {
          toast.success('Successfully checked out (Early)!', {
            icon: '👋',
            duration: 4000,
          })
        } else if (attendance.lateCheckOut) {
          toast.success('Successfully checked out (Late)!', {
            icon: '🌙',
            duration: 4000,
          })
        } else {
          toast.success('Successfully checked out!', {
            duration: 4000,
          })
        }
      }
    } catch (error: any) {
      console.error('Check-out failed:', error)

      // Extract error message from GraphQL error response
      let errorMessage = 'Failed to check out. Please try again.'

      try {
        if (error.response?.errors && Array.isArray(error.response.errors)) {
          const firstError = error.response.errors[0]
          if (firstError?.message) {
            errorMessage = firstError.message
          }
        } else if (error.message) {
          errorMessage = error.message
        }
      } catch (parseError) {
        console.error('Error while parsing error:', parseError)
      }

      toast.error(errorMessage, {
        duration: 5000,
      })
    } finally {
      setCheckInOutLoading(false)
      setShowConfirmDialog(false)
      setCheckoutReason('')
    }
  }

  const handleConfirmCheckout = () => {
    performCheckOut()
  }

  const handleCancelCheckout = () => {
    setShowConfirmDialog(false)
    setCheckoutReason('')
  }

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          button: 'h-8 px-3 text-sm',
          icon: 'h-3 w-3',
          text: 'text-xs',
          clock: 'text-sm'
        }
      case 'lg':
        return {
          button: 'h-12 px-6 text-lg',
          icon: 'h-5 w-5',
          text: 'text-base',
          clock: 'text-xl'
        }
      default: // md
        return {
          button: 'h-10 px-4',
          icon: 'h-4 w-4',
          text: 'text-sm',
          clock: 'text-base'
        }
    }
  }

  const sizeClasses = getSizeClasses()

  const compactVariant = (
    <div className={`flex items-center gap-2 ${className}`}>
      {showClock && (
        <div className={`${sizeClasses.text} font-mono`}>
          <LiveClock />
        </div>
      )}
      {!isCheckedIn ? (
        <Button
          onClick={handleCheckIn}
          className={`bg-green-600 hover:bg-green-700 ${sizeClasses.button}`}
          disabled={checkInOutLoading}
        >
          {checkInOutLoading ? (
            <RefreshCw className={`${sizeClasses.icon} mr-2 animate-spin`} />
          ) : (
            <Play className={`${sizeClasses.icon} mr-2`} />
          )}
          Check In
        </Button>
      ) : (
        <Button
          onClick={handleCheckOut}
          variant="destructive"
          className={sizeClasses.button}
          disabled={checkInOutLoading}
        >
          {checkInOutLoading ? (
            <RefreshCw className={`${sizeClasses.icon} mr-2 animate-spin`} />
          ) : (
            <Pause className={`${sizeClasses.icon} mr-2`} />
          )}
          Check Out
        </Button>
      )}
    </div>
  )

  const fullVariant = (
    <div className={`flex gap-3 items-center ${className}`}>
      {showClock && (
        <div className="text-right">
          <div className="mb-0 text-lg-right text-md-right f-18 font-weight-bold text-dark-grey d-grid align-items-center"
            style={{ display: "grid", textAlign: "right" }}
          >
            <input type="hidden" id="current-latitude" name="current_latitude" value="29.01416174079866" autoComplete="off" />
            <input type="hidden" id="current-longitude" name="current_longitude" value="77.76392005178063" autoComplete="off" />

            <span id="dashboard-clock" className={sizeClasses.clock}>
              <LiveClock />
            </span>
            <span className="f-10 font-weight-normal" style={{ fontSize: "10px", opacity: "0.5" }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long' })}
            </span>

            {showStatus && (
              <>
                {/* Show clock-out time if clocked out, clock-in time if still clocked in, nothing if neither */}
                {checkOutTime ? (
                  <span className="f-11 font-weight-normal text-lightest" style={{ fontSize: "11px", opacity: "0.6" }}>
                    Clocked Out at - {checkOutTime}
                    {attendanceStatus.lateCheckOut && (
                      <span className="text-yellow-600"> (Late)</span>
                    )}
                    {attendanceStatus.earlyCheckOut && (
                      <span className="text-green-600"> (Early)</span>
                    )}
                  </span>
                ) : checkInTime ? (
                  <span className="f-11 font-weight-normal text-lightest" style={{ fontSize: "11px", opacity: "0.6" }}>
                    Clock In at - {checkInTime}
                    {attendanceStatus.lateCheckIn && (
                      <span className="text-yellow-600"> (Late)</span>
                    )}
                    {attendanceStatus.earlyCheckIn && (
                      <span className="text-green-600"> (Early)</span>
                    )}
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      {!isCheckedIn ? (
        <Button
          onClick={handleCheckIn}
          className={`bg-green-600 hover:bg-green-700 ${sizeClasses.button}`}
          disabled={checkInOutLoading}
        >
          {checkInOutLoading ? (
            <RefreshCw className={`${sizeClasses.icon} mr-2 animate-spin`} />
          ) : (
            <Play className={`${sizeClasses.icon} mr-2`} />
          )}
          Check In
        </Button>
      ) : (
        <Button
          onClick={handleCheckOut}
          variant="destructive"
          className={sizeClasses.button}
          disabled={checkInOutLoading}
        >
          {checkInOutLoading ? (
            <RefreshCw className={`${sizeClasses.icon} mr-2 animate-spin`} />
          ) : (
            <Pause className={`${sizeClasses.icon} mr-2`} />
          )}
          Check Out
        </Button>
      )}
    </div>
  )

  return (
    <>
      {variant === 'compact' ? compactVariant : fullVariant}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Early Checkout Confirmation</DialogTitle>
            <DialogDescription>
              You have worked less than 9 hours today. Are you sure you want to check out?
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="reason" className="text-sm font-medium">
                Reason for early checkout (optional):
              </label>
              <Textarea
                id="reason"
                placeholder="Please provide a reason for early checkout..."
                value={checkoutReason}
                onChange={(e) => setCheckoutReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelCheckout}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCheckout}
              disabled={checkInOutLoading}
            >
              {checkInOutLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Confirm Checkout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}